import React, { useEffect, useRef, useState } from "react";
import Confetti from "react-confetti";
import { themePalette } from "../../../theme";
import { FetalSexResultConfettiLayer } from "./FetalSexResultBlock.styles";

interface ConfettiSize {
  height: number;
  width: number;
}

// Matches the 328×250 confetti region from the design until the layer is
// measured.
const DEFAULT_CONFETTI_SIZE: ConfettiSize = { height: 250, width: 328 };

// Absolutely-positioned confetti layer that fills its nearest positioned
// ancestor (inset: 0) and sizes the confetti canvas to itself. Siblings need
// position: relative + z-index: 1 to render above it.
export const FetalSexConfettiLayer = (): React.ReactElement => {
  const layerRef = useRef<HTMLDivElement | null>(null);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(
    () =>
      window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches ?? false,
  );
  const [confettiSize, setConfettiSize] = useState<ConfettiSize>(
    DEFAULT_CONFETTI_SIZE,
  );

  useEffect(() => {
    const mediaQuery = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mediaQuery) {
      return undefined;
    }
    const updatePreference = (): void => {
      setPrefersReducedMotion(mediaQuery.matches);
    };
    updatePreference();
    mediaQuery.addEventListener?.("change", updatePreference);
    return () => mediaQuery.removeEventListener?.("change", updatePreference);
  }, []);

  useEffect(() => {
    const layerElement = layerRef.current;
    if (!layerElement) {
      return undefined;
    }

    const updateConfettiSize = (): void => {
      setConfettiSize({
        height: Math.ceil(layerElement.offsetHeight),
        width: Math.ceil(layerElement.offsetWidth),
      });
    };

    updateConfettiSize();

    if (!window.ResizeObserver) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(updateConfettiSize);
    resizeObserver.observe(layerElement);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <FetalSexResultConfettiLayer
      data-testid="fetal-sex-result-confetti"
      ref={layerRef}
    >
      {!prefersReducedMotion && (
        <Confetti
          colors={[themePalette.mangoAccent, themePalette.tealAccent]}
          gravity={0.1}
          height={confettiSize.height}
          initialVelocityX={5}
          initialVelocityY={5}
          numberOfPieces={100}
          recycle={false}
          width={confettiSize.width}
        />
      )}
    </FetalSexResultConfettiLayer>
  );
};
